/**
 * Pinça na cena (docs/spec/mobile.md): dois dedos dão o zoom e movem a vista juntos. O ponto do
 * mapa que estava entre os dedos no começo continua entre eles; a distância entre os dedos dá o
 * zoom (dentro dos limites da cena).
 *
 * Coordenadas em px, relativas à área da cena (as mesmas da vista: `x`/`y` é o deslocamento do
 * mapa, `zoom` a escala).
 */
export type PinchView = { x: number; y: number; zoom: number };
export type Point = { x: number; y: number };
export type PinchStart = { view: PinchView; a: Point; b: Point };

const dist = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** A vista com os dedos em `a` e `b`, para a pinça que começou em `start`. */
export function pinchView(start: PinchStart, a: Point, b: Point, limits: { min: number; max: number }): PinchView {
  const d0 = dist(start.a, start.b);
  const scale = d0 > 0 ? dist(a, b) / d0 : 1;
  const zoom = Math.min(limits.max, Math.max(limits.min, start.view.zoom * scale));
  const k = zoom / start.view.zoom;
  const m0 = mid(start.a, start.b);
  const m1 = mid(a, b);
  return { zoom, x: m1.x - (m0.x - start.view.x) * k, y: m1.y - (m0.y - start.view.y) * k };
}
